import { apolloClient } from '~/apollo-client';
import {
  Flex_FetchPersonalizedSignupContractSetFromTemplateQuery,
  Flex_FetchPersonalizedSignupContractSetFromTemplateQueryVariables,
  QueryPersonalizedSignupContractSetArgs,
} from '~/typings/graphql';
import { trackError } from '~/utils/tracking';
import { FETCH_PERSONALIZED_SIGNUP_CONTRACT_SET } from './fetch-personalized-signup-contract-set-from-template.queries';

export interface ContractVersionLanguage {
  id: number;
}

export interface PerchPersonalizedFileEntry {
  utilityAccountId?: number | null;
  base64PerchPersonalizedFile?: string | null;
  perchPersonalizedError?: string | null;
}

export interface SignupContractVersion {
  id: number;
  displayName: string;
  internalName: string;
  contractType: string;
  fileUrl: string;
  notes?: string | null;
  scrollRequired: boolean;
  signatureType: string;
  contractId: number;
  version: number;
  stateId?: number | null;
  hasPersonalization?: boolean | null;
  base64PerchPersonalizedFile?: string | null;
  presignedUrl?: string | null;
  base64PerchPersonalizedFiles?: PerchPersonalizedFileEntry[] | null;
  perchPersonalizedError?: string | null;
  contractVersionLanguage?: ContractVersionLanguage | null;
  // Derived convenience field (flattened id from contractVersionLanguage?.id)
  contractVersionLanguageId?: number | null;
}

export interface ContractSet {
  contracts: (SignupContractVersion | null)[] | null;
}

export interface FetchPersonalizedSignupContractSetResponse {
  contractSet: ContractSet | null;
}

export interface FetchPersonalizedSignupContractSetParams {
  signupContractTemplateId: number;
  languageShortCode: string;
  planId: number;
}

export const fetchPersonalizedSignupContractSet = async (
  params: FetchPersonalizedSignupContractSetParams,
): Promise<FetchPersonalizedSignupContractSetResponse> => {
  try {
    const { signupContractTemplateId, languageShortCode, planId } = params;

    const { data } = await apolloClient.query<
      Flex_FetchPersonalizedSignupContractSetFromTemplateQuery,
      Flex_FetchPersonalizedSignupContractSetFromTemplateQueryVariables
    >({
      query: FETCH_PERSONALIZED_SIGNUP_CONTRACT_SET,
      variables: {
        input: {
          signupContractTemplateId,
          languageShortCode,
          planId,
        },
      } as QueryPersonalizedSignupContractSetArgs,
      fetchPolicy: 'no-cache',
    });

    const contractSet = data.personalizedSignupContractSet?.contractSet;
    if (!contractSet) {
      trackError(new Error('No contract set found for the provided template ID'));
      throw new Error('Something went wrong');
    }
    const { contracts } = contractSet;

    // Map nested contractVersionLanguage.id to a flattened contractVersionLanguageId
    const mappedContracts = (contracts || [])
      .filter(contract => contract?.signatureType !== 'sign_now')
      .map(contract =>
        contract
          ? {
              ...contract,
              contractVersionLanguageId: contract.contractVersionLanguage?.id ?? null,
            }
          : null,
      );

    return { contractSet: { contracts: mappedContracts } };
  } catch (error) {
    throw new Error(error.message);
  }
};
